"use client";

import * as Dialog from "@radix-ui/react-dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import { XIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactElement, type ReactNode, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

export function GenerationSettings({
	trigger,
	children,
	onOpenChange,
	modal = true,
}: {
	trigger: ReactElement;
	children: ReactNode;
	onOpenChange?: (open: boolean) => void;
	modal?: boolean;
}) {
	const t = useTranslations();
	const isMobile = useMediaQuery("(max-width: 767px)", {
		initializeWithValue: false,
	});
	const isLargeScreen = useMediaQuery("(min-width: 1024px)", {
		initializeWithValue: false,
	});
	const [open, setOpen] = useState(false);
	const handleOpenChange = (nextOpen: boolean) => {
		setOpen(nextOpen);
		onOpenChange?.(nextOpen);
	};

	if (isMobile) {
		return (
			<Dialog.Root open={open} onOpenChange={handleOpenChange}>
				<Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
					<Dialog.Content
						aria-describedby={undefined}
						className="fixed inset-x-0 bottom-0 z-50 flex max-h-[calc(100svh-1rem)] flex-col rounded-t-3xl bg-white shadow-xl outline-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom"
					>
						<div className="flex shrink-0 items-center justify-end px-3">
							<Dialog.Title className="sr-only">
								{t("app.userMenu.settings")}
							</Dialog.Title>
							<Dialog.Close className="flex size-11 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
								<XIcon className="size-5" />
								<span className="sr-only">
									{t("shared.actions.close")}
								</span>
							</Dialog.Close>
						</div>
						<div className="min-h-0 overflow-y-auto overscroll-contain px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] [&>div]:space-y-2 [&>div>div]:space-y-1.5 [&>div>div]:rounded-2xl [&>div>div]:bg-zinc-50 [&>div>div]:p-2 [&_button]:min-h-11">
							{children}
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		);
	}

	return (
		<DropdownMenu open={open} onOpenChange={handleOpenChange} modal={modal}>
			<DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				side={isLargeScreen ? "right" : "top"}
				sideOffset={isLargeScreen ? 12 : 4}
				className="w-[var(--radix-dropdown-menu-trigger-width)] rounded-[20px] border-0 p-2.5 shadow-[0_12px_40px_rgba(15,9,107,0.10)]"
			>
				{children}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
